import type { EventItemsSelection } from './booking-utils/types';
import { useGetUserPaymentStatusLazyQuery } from '~/graphql/catalog/queries';
import { Types, Fragments } from '~/graphql';
import { useEffect, useState } from 'react';
import { RoutingUtils } from './routing';
import { useRouter } from 'next/router';

// LS here means LocalStorage
type BookingPaymentPropsLS = {
    selectionIds: EventItemsSelection;
    userBooking: Fragments.BookingFieldsFragment | null | undefined;
};

// LS here means LocalStorage
type BookingPaymentProps = {
    selectionIdsFromLS: EventItemsSelection | null;
    userBookingFromLS: Fragments.BookingFieldsFragment | null | undefined;
};

const getPaymentPropsLSKey = (userId: Types.UUID, eventId: Types.UUID) =>
    `booking-payment-data-${userId}-${eventId}`;

// LS here means LocalStorage
export const saveBookingPaymentDataToLS = (props: {
    selectionIds: EventItemsSelection;
    userBooking: Fragments.BookingFieldsFragment | null | undefined;
    userId: Types.UUID;
    eventId: Types.UUID;
}) => {
    const { userId, eventId, selectionIds, userBooking } = props;
    const key = getPaymentPropsLSKey(userId, eventId);
    const successPageProps: BookingPaymentPropsLS = { selectionIds, userBooking };
    localStorage.setItem(key, JSON.stringify(successPageProps));
};

/**
 * Some payment methods (paypal) require redirect from our site and then going back to booking success page
 * Here we are handling retrieving props for success page that were set before the redirect
 * @param userId
 * @param eventId
 * @param shouldGetPropsFromLS - LS means LocalStorage
 */
export const getBookingPaymentProps = (
    userId: Types.UUID | undefined,
    eventId: Types.UUID,
    shouldGetPropsFromLS: boolean
): BookingPaymentProps => {
    const defaultResult: BookingPaymentProps = {
        selectionIdsFromLS: null,
        userBookingFromLS: null
    };

    if (!shouldGetPropsFromLS || !userId) {
        return defaultResult;
    }

    const key = getPaymentPropsLSKey(userId, eventId);
    try {
        const lsString = localStorage.getItem(key);
        if (!lsString) {
            return defaultResult;
        }
        const { userBooking, selectionIds } = JSON.parse(lsString) as BookingPaymentPropsLS;
        localStorage.removeItem(key);
        return { userBookingFromLS: userBooking, selectionIdsFromLS: selectionIds };
    } catch (e) {
        console.error('Error while retrieving props for success page', e);
        return defaultResult;
    }
};

export const useBookingPaymentStatus = (
    userId: Types.UUID | null | undefined
): {
    paymentStatus: Types.PaymentStatus | undefined;
    isPaymentErrorFlow: boolean;
    isPaymentSuccessFlow: boolean;
    userPaymentStatusLoading: boolean;
    clientReference: string | null;
} => {
    const router = useRouter();
    const { payment_intent, payment_intent_client_secret, redirect_status } = router.query;
    const [getUserPaymentStatusQuery, { data: userPaymentStatusData, loading: isLoading }] =
        useGetUserPaymentStatusLazyQuery();
    const paymentStatus = userPaymentStatusData?.userPaymentStatus.paymentStatus;

    const getQueryValue = (queryValueRaw: unknown): string | undefined =>
        typeof queryValueRaw === 'string' ? queryValueRaw : undefined;

    const [paymentIntent] = useState(getQueryValue(payment_intent));
    const [paymentIntentSecret] = useState(getQueryValue(payment_intent_client_secret));
    const [redirectStatus] = useState(getQueryValue(redirect_status));

    const isPaymentSuccessFlow = !!(
        redirectStatus === 'succeeded' &&
        paymentIntent &&
        paymentIntentSecret
    );

    const isPaymentErrorFlow = !!(
        redirectStatus === 'failed' &&
        paymentIntent &&
        paymentIntentSecret
    );

    /**
     * Fetch payment status
     */
    useEffect(() => {
        if (isPaymentSuccessFlow && userId && paymentStatus !== 'PAID') {
            const intervalId = setInterval(async () => {
                const paymentStatusResponse = await getUserPaymentStatusQuery({
                    variables: {
                        userId,
                        paymentReference: paymentIntent
                    }
                });
                const { paymentStatus: newPaymentStatus = null } = paymentStatusResponse.data
                    ?.userPaymentStatus || { paymentStatus: null };
                if (newPaymentStatus !== paymentStatus) {
                    if (newPaymentStatus === 'PAID') {
                        clearInterval(intervalId);
                    }
                }
            }, 2500);
            return () => {
                clearInterval(intervalId);
            };
        }
    }, [userId, isPaymentSuccessFlow, paymentStatus]);

    /**
     * Clear query params after reading it
     */
    useEffect(() => {
        if (
            typeof payment_intent === 'string' &&
            typeof payment_intent_client_secret === 'string' &&
            typeof redirect_status === 'string'
        ) {
            setTimeout(() => {
                RoutingUtils.RemoveQueryParams(router, {
                    payment_intent,
                    payment_intent_client_secret,
                    redirect_status
                });
            }, 100);
        }
    }, [payment_intent, payment_intent_client_secret, redirect_status]);

    return {
        paymentStatus,
        userPaymentStatusLoading: isLoading || (!paymentStatus && isPaymentSuccessFlow),
        isPaymentSuccessFlow,
        isPaymentErrorFlow,
        clientReference:
            typeof payment_intent_client_secret === 'string'
                ? payment_intent_client_secret
                : Array.isArray(payment_intent_client_secret)
                  ? (payment_intent_client_secret[0] ?? null)
                  : null
    };
};

export const isPercentagePromoCode = (promoCode: Fragments.PromoCodeForOrderFieldsFragment) => {
    return (
        promoCode.promoCodeType === Types.PromoCodeType.Percentage ||
        promoCode.promoCodeType === Types.PromoCodeType.LimitedPercentage
    );
};
